export default function initHeader() {
    type JQueryLike = {
        on: (eventName: string, handler: (...args: unknown[]) => void) => void;
        off: (eventName: string, handler: (...args: unknown[]) => void) => void;
    };

    type WindowWithJQuery = Window & {
        jQuery?: (target: Element | Document | string) => JQueryLike;
        wp?: {
            data?: {
                select?: (storeName: string) => unknown;
                subscribe?: (listener: () => void) => () => void;
            };
        };
    };

    const body = document.body;
    const nav = document.querySelector('.nav-closed');
    const navButton = document.querySelector('.mobile-nav-button');
    const bars = [
        document.querySelector('.bar1'),
        document.querySelector('.bar2'),
        document.querySelector('.bar3'),
    ].filter(Boolean);
    const header = document.querySelector('#header') as HTMLDivElement;
    const headerSpacing = document.querySelector('.header-spacing') as HTMLDivElement;
    const cartMenu = document.querySelector('.cart-menu') as HTMLDetailsElement | null;
    const cartLink = cartMenu?.querySelector('.cart-link') as HTMLElement | null;
    const mobileNav = document.querySelector('.site-nav__mobile') as HTMLElement | null;
    const mobileNavOpen = document.querySelector('.site-nav__mobile-open') as HTMLButtonElement | null;
    const mobileNavClose = document.querySelector('.site-nav__mobile-close') as HTMLButtonElement | null;
    const mobileNavBackdrop = document.querySelector('.site-nav__mobile-backdrop') as HTMLElement | null;
    const mobileBranchToggles = Array.from(
        document.querySelectorAll<HTMLButtonElement>('.site-nav__mobile .site-nav__branch-toggle')
    );
    const desktopMenuButtons = Array.from(
        document.querySelectorAll<HTMLButtonElement>(
            '.site-nav__primary > .site-nav__desktop-item--panel > .site-nav__branch-row > .site-nav__branch-toggle'
        )
    );
    const desktopMenuItems = Array.from(
        document.querySelectorAll<HTMLElement>(
            '.site-nav__primary > .site-nav__desktop-item--panel'
        )
    );
    const searchForm = document.querySelector('.site-nav-search') as HTMLFormElement | null;
    const searchInput = searchForm?.querySelector('input[type="search"]') as HTMLInputElement | null;
    const searchResults = searchForm?.querySelector('.site-nav-search__results') as HTMLElement | null;
    const searchToggle = document.querySelector<HTMLButtonElement>('.site-nav-search-toggle');
    const searchClose = searchForm?.querySelector<HTMLButtonElement>('.site-nav-search__close');

    function setMobileSearch(open: boolean, restoreFocus = false) {
        searchForm?.classList.toggle('is-open', open);
        searchToggle?.setAttribute('aria-expanded', String(open));
        searchToggle?.setAttribute('aria-label', open ? 'Close search' : 'Open search');
        if (open) searchInput?.focus();
        else {
            window.clearTimeout(searchTimer);
            searchController?.abort();
            closeSearchResults();
            if (restoreFocus) searchToggle?.focus();
        }
        updateHeaderSpacing();
    }

    function toggleMobileSearch() {
        setMobileSearch(searchToggle?.getAttribute('aria-expanded') !== 'true', true);
    }

    function closeMobileSearch() {
        setMobileSearch(false, true);
    }
    const win = window as WindowWithJQuery;
    const wooQuery = win.jQuery;
    let cartCloseTimer: number | undefined;
    let cartAnimationTimer: number | undefined;
    let addedButtonTimer: number | undefined;
    let cartSyncTimers: number[] = [];
    let cartOverlayOpenTimer: number | undefined;
    let blockCartCount: number | null = null;
    let isCartOverlayViewportActive = isMobileCartMenu();
    let mobileNavCloseTimer: number | undefined;
    let desktopMenuTimer: number | undefined;
    let lastNavFocus: HTMLElement | null = null;
    let searchTimer: number | undefined;
    let searchController: AbortController | null = null;

    function setBranch(button: HTMLButtonElement, open: boolean) {
        const controls = button.getAttribute('aria-controls');
        const panel = controls ? document.getElementById(controls) : null;

        button.setAttribute('aria-expanded', String(open));
        if (panel) panel.hidden = !open;
    }

    function getDirectBranchToggle(branch: Element) {
        const row = Array.from(branch.children).find((child) =>
            child.classList.contains('site-nav__branch-row')
        );

        return (
            Array.from(row?.children || []).find(
                (child) =>
                    child instanceof HTMLButtonElement &&
                    child.classList.contains('site-nav__branch-toggle')
            ) || null
        ) as HTMLButtonElement | null;
    }

    function onMobileBranchToggle(event: MouseEvent) {
        const button = event.currentTarget as HTMLButtonElement;
        const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
        const branch = button.closest('.site-nav__branch');
        const siblings = branch?.parentElement?.children || [];

        Array.from(siblings).forEach((sibling) => {
            const siblingButton = getDirectBranchToggle(sibling);
            if (siblingButton && siblingButton !== button) setBranch(siblingButton, false);
        });

        setBranch(button, shouldOpen);
    }

    function openMobileNav() {
        if (!mobileNav || !mobileNavOpen) return;

        setMobileSearch(false);

        window.clearTimeout(mobileNavCloseTimer);
        lastNavFocus = document.activeElement as HTMLElement | null;
        mobileNav.hidden = false;
        mobileNavOpen.setAttribute('aria-expanded', 'true');
        body.classList.add('site-nav-open');
        window.requestAnimationFrame(() => mobileNavClose?.focus());
    }

    function closeMobileNav(restoreFocus = true) {
        if (!mobileNav || !mobileNavOpen) return;

        body.classList.remove('site-nav-open');
        mobileNavOpen.setAttribute('aria-expanded', 'false');
        mobileNavCloseTimer = window.setTimeout(() => {
            mobileNav.hidden = true;
        }, 250);
        if (restoreFocus) (lastNavFocus || mobileNavOpen).focus();
    }

    function onMobileNavClose() {
        closeMobileNav();
    }

    function closeDesktopMenus(except?: HTMLButtonElement) {
        desktopMenuButtons.forEach((button) => {
            if (button !== except) setBranch(button, false);
        });
    }

    function openDesktopMenu(button: HTMLButtonElement) {
        window.clearTimeout(desktopMenuTimer);
        closeDesktopMenus(button);
        setBranch(button, true);
    }

    function scheduleDesktopMenuClose() {
        window.clearTimeout(desktopMenuTimer);
        desktopMenuTimer = window.setTimeout(() => closeDesktopMenus(), 180);
    }

    function cancelDesktopMenuClose() {
        window.clearTimeout(desktopMenuTimer);
    }

    function onDesktopMenuButtonClick(event: MouseEvent) {
        const button = event.currentTarget as HTMLButtonElement;
        const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
        if (shouldOpen) {
            openDesktopMenu(button);
        } else {
            setBranch(button, false);
        }
    }

    function onDesktopMenuEnter(event: PointerEvent) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const button = getDirectBranchToggle(event.currentTarget as HTMLElement);
        if (!button) return;
        window.clearTimeout(desktopMenuTimer);
        desktopMenuTimer = window.setTimeout(() => openDesktopMenu(button), 120);
    }

    function onDesktopMenuFocusOut(event: FocusEvent) {
        if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
            scheduleDesktopMenuClose();
        }
    }

    function trapMobileNavFocus(event: KeyboardEvent) {
        if (event.key !== 'Tab' || !body.classList.contains('site-nav-open') || !mobileNav) return;
        const focusable = Array.from(
            mobileNav.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((element) => !element.closest('[hidden]'));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    function closeSearchResults() {
        if (!searchResults || !searchInput) return;
        window.clearTimeout(searchTimer);
        searchController?.abort();
        searchResults.removeAttribute('aria-busy');
        searchResults.hidden = true;
        searchInput.setAttribute('aria-expanded', 'false');
    }

    type SearchResult = {
        type: string;
        name: string;
        url: string;
        image?: string;
        image_alt?: string;
    };

    function renderSearchLoading() {
        if (!searchResults || !searchInput) return;
        const row = document.createElement('div');
        row.className = 'site-nav-search__skeleton';
        row.setAttribute('aria-hidden', 'true');
        const content = document.createElement('span');
        content.className = 'site-nav-search__result-content';
        for (const part of ['type', 'name']) {
            const line = document.createElement('span');
            line.className = `site-nav-search__skeleton-${part}`;
            content.appendChild(line);
        }
        const image = document.createElement('span');
        image.className = 'site-nav-search__skeleton-image';
        row.append(content, image);
        const status = document.createElement('span');
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        status.textContent = 'Searching products, categories and ranges.';
        searchResults.replaceChildren(row, status);
        searchResults.setAttribute('aria-busy', 'true');
        searchResults.hidden = false;
        searchInput.setAttribute('aria-expanded', 'true');
    }

    function renderSearchResults(items: SearchResult[]) {
        if (!searchResults || !searchInput) return;
        searchResults.removeAttribute('aria-busy');
        searchResults.replaceChildren();

        if (!items.length) {
            const message = document.createElement('p');
            message.textContent = 'No matching products, categories or ranges.';
            searchResults.appendChild(message);
        } else {
            items.forEach((item) => {
                const link = document.createElement('a');
                const content = document.createElement('span');
                const type = document.createElement('small');
                const name = document.createElement('span');
                link.href = item.url;
                link.setAttribute('role', 'option');
                content.className = 'site-nav-search__result-content';
                type.textContent = item.type;
                name.textContent = item.name;

                if (item.image) {
                    const image = document.createElement('img');
                    image.src = item.image;
                    image.alt = item.image_alt || '';
                    image.width = 64;
                    image.height = 64;
                    image.decoding = 'async';
                    link.append(content, image);
                } else {
                    link.classList.add('site-nav-search__result--no-image');
                    link.append(content);
                }

                content.append(type, name);
                searchResults.appendChild(link);
            });
        }

        searchResults.hidden = false;
        searchInput.setAttribute('aria-expanded', 'true');
    }

    function onSearchInput() {
        if (!searchForm || !searchInput) return;
        const endpoint = searchForm.dataset.searchEndpoint;
        const query = searchInput.value.trim();
        window.clearTimeout(searchTimer);
        searchController?.abort();
        closeSearchResults();

        if (!endpoint || query.length < 2) {
            closeSearchResults();
            return;
        }

        searchTimer = window.setTimeout(async () => {
            const controller = new AbortController();
            searchController = controller;
            renderSearchLoading();
            const url = new URL(endpoint, window.location.href);
            url.searchParams.set('action', 'clarity_header_search');
            url.searchParams.set('query', query);

            try {
                const response = await fetch(url.toString(), { signal: controller.signal });
                const payload = (await response.json()) as {
                    success?: boolean;
                    data?: SearchResult[];
                };
                if (controller.signal.aborted) return;
                if (response.ok && payload.success && Array.isArray(payload.data)) {
                    renderSearchResults(payload.data);
                } else {
                    closeSearchResults();
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    closeSearchResults();
                }
            }
        }, 220);
    }

    function onSearchKeyDown(event: KeyboardEvent) {
        if (event.key !== 'ArrowDown' || searchResults?.hidden) return;
        const firstResult = searchResults?.querySelector<HTMLElement>('a');
        if (firstResult) {
            event.preventDefault();
            firstResult.focus();
        }
    }

    function expandCurrentMobileBranch() {
        const current = mobileNav?.querySelector('.is-current, [aria-current="page"]');
        let panel = current?.closest('ul') as HTMLElement | null;

        while (panel && mobileNav?.contains(panel)) {
            panel.hidden = false;
            const controller = mobileNav.querySelector<HTMLButtonElement>(
                `[aria-controls="${panel.id}"]`
            );
            controller?.setAttribute('aria-expanded', 'true');
            panel = panel.parentElement?.closest('ul') as HTMLElement | null;
        }
    }

    // toggle nav open/closed
    function onNavButtonClick() {
        nav?.classList.toggle('open');
        bars.forEach((bar) => bar?.classList.toggle('change'));
    }

    // Presence of a touchstart handler is enough to tell iOS/WebKit this
    // element (and its interactive descendants) should respond on the
    // first tap, instead of eating the first tap to set :hover/:focus.
    // Deliberately does nothing — click handlers own all real behavior.
    function onCartMenuTouchStart() {}
    cartMenu?.addEventListener('touchstart', onCartMenuTouchStart, { passive: true });

    // close nav when clicking outside
    function onBodyClick(e: PointerEvent) {
        const target = e.target as Element | null;

        const isNavButton = target?.closest('.mobile-nav-button');
        const isCartMenu = target?.closest('.cart-menu');

        if (!isNavButton && nav?.classList.contains('open')) {
            nav.classList.remove('open');

            bars.forEach((bar) => {
                bar?.classList.remove('change');
            });
        }

        if (!isCartMenu && cartMenu?.open) {
            closeCartMenuImmediately();
        }

        if (!target?.closest('.site-nav__desktop-item--panel')) {
            closeDesktopMenus();
        }

        if (!target?.closest('.site-nav-search')) {
            closeSearchResults();
        }
    }

    // sync header spacing to header height
    function updateHeaderSpacing() {
        if (header && headerSpacing) {
            headerSpacing.style.paddingTop = `${header.offsetHeight}px`;
            document.documentElement.style.setProperty(
                '--clarity-header-offset',
                `${Math.ceil(header.getBoundingClientRect().bottom)}px`
            );
        }
    }

    // swap tactile/desktop class based on viewport width
    function updateHeaderClass() {
        if (!header) return;
        const isMobile = window.innerWidth < 992;
        header.classList.toggle('tactile', isMobile);
        header.classList.toggle('desktop', !isMobile);
    }

    function onResize() {
        updateHeaderSpacing();
        updateHeaderClass();
        if (window.innerWidth >= 992 && searchForm?.classList.contains('is-open')) {
            setMobileSearch(false);
        }
        const nextIsCartOverlayViewport = isMobileCartMenu();

        if (cartMenu?.open && nextIsCartOverlayViewport !== isCartOverlayViewportActive) {
            closeCartMenuImmediately();
        }

        isCartOverlayViewportActive = nextIsCartOverlayViewport;
        syncCartMenuOverlayState();

        if (!isMobileCartMenu() && body.classList.contains('site-nav-open')) {
            closeMobileNav(false);
        }
    }

    function isDesktopCartMenu() {
        return !isMobileCartMenu();
    }

    function isMobileCartMenu() {
        return window.innerWidth < 992;
    }

    function syncCartMenuOverlayState() {
        body.classList.toggle('cart-menu-open', Boolean(cartMenu?.open && isMobileCartMenu()));
    }

    function closeCartMenuImmediately() {
        if (!cartMenu) return;

        window.clearTimeout(cartCloseTimer);
        window.clearTimeout(cartOverlayOpenTimer);
        cartMenu.open = false;
    }

    function openCartMenuOverlay() {
        if (!cartMenu) return;

        window.clearTimeout(cartCloseTimer);
        cartMenu.open = true;
        syncCartMenuOverlayState();
    }

    function scheduleCartMenuOverlayOpen() {
        openCartMenuOverlay();
        window.clearTimeout(cartOverlayOpenTimer);
        cartOverlayOpenTimer = window.setTimeout(() => {
            openCartMenuOverlay();
        }, 0);
    }

    function openCartMenu() {
        if (!cartMenu || !isDesktopCartMenu()) return;

        window.clearTimeout(cartCloseTimer);
        cartMenu.open = true;
    }

    function closeCartMenu() {
        if (!cartMenu || !isDesktopCartMenu()) return;

        cartCloseTimer = window.setTimeout(() => {
            cartMenu.open = false;
        }, 120);
    }

    function onCartFocusOut(event: FocusEvent) {
        if (!cartMenu || cartMenu.contains(event.relatedTarget as Node | null)) {
            return;
        }

        closeCartMenu();
    }

    function onCartMenuToggle() {
        syncCartMenuOverlayState();
    }

    function onCartMenuClick(event: MouseEvent) {
        const target = event.target as Element | null;

        if (!target?.closest('.cart-menu__mobile-close')) {
            return;
        }

        event.preventDefault();
        closeCartMenuImmediately();
        cartLink?.focus();
    }

    function onCartLinkClick(event: MouseEvent) {
        const target = event.target as Element | null;

        if (isMobileCartMenu()) {
            event.preventDefault();
            event.stopPropagation();
            scheduleCartMenuOverlayOpen();
            return;
        }

        const cartUrl = cartLink?.dataset.cartUrl;

        if (!target?.closest('.cart-link__icon') || !cartUrl) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        window.location.href = cartUrl;
    }

    function onDocumentKeyDown(event: KeyboardEvent) {
        trapMobileNavFocus(event);

        if (event.key !== 'Escape') return;

        if (window.innerWidth < 992 && searchForm?.classList.contains('is-open')) {
            closeMobileSearch();
            return;
        }

        if (searchResults && !searchResults.hidden) {
            closeSearchResults();
            searchInput?.focus();
            return;
        }

        if (body.classList.contains('site-nav-open')) {
            closeMobileNav();
            return;
        }

        const openDesktopButton = desktopMenuButtons.find(
            (button) => button.getAttribute('aria-expanded') === 'true'
        );
        if (openDesktopButton) {
            closeDesktopMenus();
            openDesktopButton.focus();
            return;
        }

        if (cartMenu?.open) {
            closeCartMenuImmediately();
            cartLink?.focus();
        }
    }

    function focusAccountFormFromUrl() {
        const accountIntent = new URLSearchParams(window.location.search).get('clarity_account');

        if (accountIntent !== 'login' && accountIntent !== 'signup') {
            return;
        }

        const formSelector = accountIntent === 'signup' ? 'form.register' : 'form.login';
        const form = document.querySelector(formSelector) as HTMLFormElement | null;
        const panel = form?.closest('.u-column1, .u-column2') as HTMLElement | null;
        const firstField = form?.querySelector(
            'input:not([type="hidden"])'
        ) as HTMLInputElement | null;

        if (!form) {
            return;
        }

        window.setTimeout(() => {
            (panel || form).scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstField?.focus({ preventScroll: true });
        }, 120);
    }

    function getAddedButton(candidate: unknown) {
        if (candidate instanceof HTMLElement) {
            return candidate.closest('li.product') ? candidate : null;
        }

        if (
            candidate &&
            typeof candidate === 'object' &&
            'get' in candidate &&
            typeof candidate.get === 'function'
        ) {
            const element = candidate.get(0);

            return element instanceof HTMLElement && element.closest('li.product') ? element : null;
        }

        return null;
    }

    function animateCartCount() {
        if (!cartLink) return;

        window.clearTimeout(cartAnimationTimer);
        cartLink.classList.remove('is-cart-updated');

        window.requestAnimationFrame(() => {
            cartLink.classList.add('is-cart-updated');
        });

        cartAnimationTimer = window.setTimeout(() => {
            cartLink.classList.remove('is-cart-updated');
        }, 900);
    }

    function updateCartCount(count: number) {
        const cartCount = cartLink?.querySelector('.cart-link__count');

        if (!cartLink || !cartCount) return;

        const nextCount = Math.max(0, count);
        const nextLabel = nextCount > 99 ? '99+' : String(nextCount);
        const currentLabel = cartCount.textContent?.trim();

        cartCount.textContent = nextCount > 0 ? nextLabel : '0';
        cartCount.classList.toggle('is-empty', nextCount < 1);
        cartLink.setAttribute(
            'aria-label',
            nextCount > 0 ? `Open basket preview, ${nextLabel} items` : 'Open basket preview'
        );

        if (currentLabel !== cartCount.textContent?.trim()) {
            animateCartCount();
        }
    }

    function readBlockCartCount() {
        const cartStore = win.wp?.data?.select?.('wc/store/cart') as
            | {
                  getCartData?: () => { items_count?: number; itemsCount?: number };
                  getCartTotals?: () => { items_count?: number; itemsCount?: number };
                  getCartItems?: () => Array<{ quantity?: number }>;
              }
            | undefined;
        const cartData = cartStore?.getCartData?.();
        const cartTotals = cartStore?.getCartTotals?.();
        const cartItems = cartStore?.getCartItems?.();
        const itemCount = Array.isArray(cartItems)
            ? cartItems.reduce((total, item) => total + (item.quantity || 0), 0)
            : null;
        const count =
            cartData?.items_count ??
            cartData?.itemsCount ??
            cartTotals?.items_count ??
            cartTotals?.itemsCount ??
            itemCount;

        return typeof count === 'number' && Number.isFinite(count) ? count : null;
    }

    function syncCartCountFromBlocks() {
        const nextCount = readBlockCartCount();

        if (nextCount === null || nextCount === blockCartCount) {
            return;
        }

        blockCartCount = nextCount;
        updateCartCount(nextCount);
    }

    function scheduleCartCountSync() {
        cartSyncTimers.forEach((timer) => window.clearTimeout(timer));
        cartSyncTimers = [0, 150, 450, 900].map((delay) =>
            window.setTimeout(syncCartCountFromBlocks, delay)
        );
    }

    function updateAddedButton(button: HTMLElement | null) {
        if (!button) return;

        const originalLabel =
            button.dataset.originalLabel || button.textContent?.trim() || 'Add to basket';

        button.dataset.originalLabel = originalLabel;
        button.classList.add('is-added');
        button.textContent = 'Added';
        button.setAttribute('aria-label', 'Added to basket');

        window.clearTimeout(addedButtonTimer);
        addedButtonTimer = window.setTimeout(() => {
            button.classList.remove('is-added');
            button.textContent = button.dataset.originalLabel || originalLabel;
            button.removeAttribute('aria-label');
        }, 2200);
    }

    function onWooAddedToCart(...args: unknown[]) {
        updateAddedButton(getAddedButton(args[3]));
        scheduleCartCountSync();
        animateCartCount();
    }

    function onWooBlocksCartChange() {
        scheduleCartCountSync();
    }

    function onCartRemoveClick(event: MouseEvent) {
        const target = event.target as Element | null;

        if (!target?.closest('.wc-block-cart-item__remove-link')) {
            return;
        }

        scheduleCartCountSync();
    }

    const cartBackdrop = document.createElement('div');
    cartBackdrop.className = 'cart-menu-backdrop';
    cartBackdrop.setAttribute('aria-hidden', 'true');
    body.appendChild(cartBackdrop);

    function onCartBackdropActivate() {
        closeCartMenuImmediately();
        cartLink?.focus();
    }
    cartBackdrop.addEventListener('click', onCartBackdropActivate);
    cartBackdrop.addEventListener('touchstart', onCartMenuTouchStart, { passive: true }); // same presence-fix as above

    mobileNavOpen?.addEventListener('click', openMobileNav);
    mobileNavClose?.addEventListener('click', onMobileNavClose);
    mobileNavBackdrop?.addEventListener('click', onMobileNavClose);
    mobileBranchToggles.forEach((button) =>
        button.addEventListener('click', onMobileBranchToggle)
    );
    desktopMenuButtons.forEach((button) =>
        button.addEventListener('click', onDesktopMenuButtonClick)
    );
    desktopMenuItems.forEach((item) => {
        item.addEventListener('pointerenter', onDesktopMenuEnter);
        item.addEventListener('pointerleave', scheduleDesktopMenuClose);
        item.addEventListener('focusin', cancelDesktopMenuClose);
        item.addEventListener('focusout', onDesktopMenuFocusOut);
    });
    searchInput?.addEventListener('input', onSearchInput);
    searchInput?.addEventListener('keydown', onSearchKeyDown);
    searchToggle?.addEventListener('click', toggleMobileSearch);
    searchClose?.addEventListener('click', closeMobileSearch);

    navButton?.addEventListener('click', onNavButtonClick);
    body.addEventListener('click', onBodyClick);
    window.addEventListener('resize', onResize);
    cartMenu?.addEventListener('mouseenter', openCartMenu);
    cartMenu?.addEventListener('mouseleave', closeCartMenu);
    cartMenu?.addEventListener('focusin', openCartMenu);
    cartMenu?.addEventListener('focusout', onCartFocusOut);
    cartMenu?.addEventListener('toggle', onCartMenuToggle);
    cartMenu?.addEventListener('click', onCartMenuClick);
    cartLink?.addEventListener('click', onCartLinkClick);
    document.addEventListener('keydown', onDocumentKeyDown);
    wooQuery?.(document.body).on('added_to_cart.clarityHeaderFeedback', onWooAddedToCart);
    document.body.addEventListener('wc-blocks_added_to_cart', onWooBlocksCartChange);
    document.body.addEventListener('click', onCartRemoveClick);
    const unsubscribeCartStore = win.wp?.data?.subscribe?.(syncCartCountFromBlocks);

    // run once on init to set initial state
    expandCurrentMobileBranch();
    onResize();
    document.fonts.ready.then(updateHeaderSpacing);
    focusAccountFormFromUrl();
    syncCartCountFromBlocks();

    return function cleanup() {
        navButton?.removeEventListener('click', onNavButtonClick);
        body.removeEventListener('click', onBodyClick);
        window.removeEventListener('resize', onResize);
        cartMenu?.removeEventListener('mouseenter', openCartMenu);
        cartMenu?.removeEventListener('mouseleave', closeCartMenu);
        cartMenu?.removeEventListener('focusin', openCartMenu);
        cartMenu?.removeEventListener('focusout', onCartFocusOut);
        cartMenu?.removeEventListener('toggle', onCartMenuToggle);
        cartMenu?.removeEventListener('click', onCartMenuClick);
        cartMenu?.removeEventListener('touchstart', onCartMenuTouchStart);
        cartLink?.removeEventListener('click', onCartLinkClick);
        document.removeEventListener('keydown', onDocumentKeyDown);
        wooQuery?.(document.body).off('added_to_cart.clarityHeaderFeedback', onWooAddedToCart);
        document.body.removeEventListener('wc-blocks_added_to_cart', onWooBlocksCartChange);
        document.body.removeEventListener('click', onCartRemoveClick);
        unsubscribeCartStore?.();
        window.clearTimeout(cartCloseTimer);
        window.clearTimeout(cartAnimationTimer);
        window.clearTimeout(addedButtonTimer);
        window.clearTimeout(cartOverlayOpenTimer);
        cartSyncTimers.forEach((timer) => window.clearTimeout(timer));
        body.classList.remove('cart-menu-open');
        cartBackdrop.removeEventListener('click', onCartBackdropActivate);
        cartBackdrop.removeEventListener('touchstart', onCartMenuTouchStart);
        cartBackdrop.remove();
        mobileNavOpen?.removeEventListener('click', openMobileNav);
        mobileNavClose?.removeEventListener('click', onMobileNavClose);
        mobileNavBackdrop?.removeEventListener('click', onMobileNavClose);
        mobileBranchToggles.forEach((button) =>
            button.removeEventListener('click', onMobileBranchToggle)
        );
        desktopMenuButtons.forEach((button) =>
            button.removeEventListener('click', onDesktopMenuButtonClick)
        );
        desktopMenuItems.forEach((item) => {
            item.removeEventListener('pointerenter', onDesktopMenuEnter);
            item.removeEventListener('pointerleave', scheduleDesktopMenuClose);
            item.removeEventListener('focusin', cancelDesktopMenuClose);
            item.removeEventListener('focusout', onDesktopMenuFocusOut);
        });
        searchInput?.removeEventListener('input', onSearchInput);
        searchInput?.removeEventListener('keydown', onSearchKeyDown);
        searchToggle?.removeEventListener('click', toggleMobileSearch);
        searchClose?.removeEventListener('click', closeMobileSearch);
        searchController?.abort();
        window.clearTimeout(searchTimer);
        window.clearTimeout(mobileNavCloseTimer);
        window.clearTimeout(desktopMenuTimer);
        body.classList.remove('site-nav-open');
    };
}
